import { useEffect, useMemo, useState } from 'react';
import { api, type CatalogChange, type CategoryRow, type Dietary, type SupplierOption } from '../api';
import { ts, useDebounced, useLoad } from '../lib';

// Pieces the Catalog, Bids and Associations panels share.

/** Until the cutover Magento is the system of record: say so wherever an edit is made. */
export function MagentoNote() {
  return (
    <p className="note">
      Magento is still the system of record until the cutover: an edit here does not reach it, so make the same change there too.
      The data refresh from Magento keeps what you save here, and reports anything Magento has changed since.
    </p>
  );
}

/** "12 / 4 oz" — a supplier product's pack. */
export const pack = (count: number | null | undefined, size: number | null | undefined, uom: string | null | undefined) =>
  count == null && size == null ? '—' : `${count ?? '?'} / ${size ?? '?'} ${uom ?? ''}`.trim();

export const enabled = (status: number | null | undefined) => Number(status) === 1;

export function StatusPill({ status }: { status: number | null | undefined }) {
  return enabled(status) ? <span className="pill edi">enabled</span> : <span className="pill">disabled</span>;
}

/** The dietary flags a product carries, as short pills; nothing when it carries none. */
export function DietPills({ d }: { d: Partial<Dietary> }) {
  const pills = [
    d.kosher ? (d.kosher_type ? `Kosher · ${d.kosher_type}` : 'Kosher') : null,
    d.gluten_free ? 'Gluten-free' : null,
    d.vegan ? 'Vegan' : null,
  ].filter(Boolean) as string[];
  if (!pills.length) return <span className="muted">—</span>;
  return <span className="diet">{pills.map((p) => <span key={p} className="pill cat-tag">{p}</span>)}</span>;
}

/** A yes / no / not set select for a nullable flag. */
export function TriSelect({ value, onChange, label }: { value: boolean | null | undefined; onChange: (v: boolean | null) => void; label: string }) {
  return (
    <select className="sel" aria-label={label} value={value == null ? '' : value ? '1' : '0'}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === '1')}>
      <option value="">—</option>
      <option value="1">Yes</option>
      <option value="0">No</option>
    </select>
  );
}

/** Previous / next over a paged list. */
export function Pager({ page, per, total, onPage }: { page: number; per: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / per));
  return (
    <div className="iw-pager">
      <button className="btn-sm ghost" disabled={page <= 1} onClick={() => onPage(page - 1)}>← Prev</button>
      <span className="muted">Page {page.toLocaleString()} of {pages.toLocaleString()} · {total.toLocaleString()} rows</span>
      <button className="btn-sm ghost" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next →</button>
    </div>
  );
}

/** A supplier typeahead (commerce vendors). `value` is the picked supplier, or null. */
export function SupplierPicker({ value, onChange, label = 'Supplier', placeholder = 'Search suppliers…' }: {
  value: SupplierOption | null; onChange: (s: SupplierOption | null) => void; label?: string; placeholder?: string;
}) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [opts, setOpts] = useState<SupplierOption[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    let live = true;
    api.catalogSuppliers(dq).then((r) => live && setOpts(r)).catch(() => live && setOpts([]));
    return () => { live = false; };
  }, [dq, open]);
  if (value) {
    return (
      <span className="field"><span>{label}</span>
        <span className="picked-row" style={{ margin: 0 }}>
          <button type="button" className="pill pick" title="Clear" onClick={() => onChange(null)}>{value.name ?? `Supplier ${value.id}`} ✕</button>
        </span>
      </span>
    );
  }
  return (
    <label className="field"><span>{label}</span>
      <input className="search" placeholder={placeholder} value={q} onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} />
      {open && opts.length > 0 && (
        <div className="typeahead">
          {opts.map((o) => (
            <button type="button" key={o.id} className="ta-opt" onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(o); setQ(''); setOpen(false); }}>
              {o.name ?? `Supplier ${o.id}`} <span className="mono">#{o.id}</span>{o.is_active === false ? <span className="muted"> · inactive</span> : null}
            </button>
          ))}
        </div>
      )}
    </label>
  );
}

/** Who changed what on a record (commerce ops_change_log). */
export function ChangeLog({ changes }: { changes: CatalogChange[] }) {
  if (!changes.length) return null;
  const show = (v: string | null) => (v == null ? '—' : v.length > 80 ? `${v.slice(0, 80)}…` : v);
  return (
    <section className="card">
      <h2>Recent changes</h2>
      <table>
        <thead><tr><th>When</th><th>Who</th><th>What</th><th>From</th><th>To</th></tr></thead>
        <tbody>
          {changes.map((c, i) => (
            <tr key={i}>
              <td className="mono">{ts(c.changed_at)}</td>
              <td>{c.changed_by}</td>
              <td>{c.field === '(created)' ? 'created' : c.field === '(deleted)' ? 'removed' : c.field.replace(/_/g, ' ')}</td>
              <td className="mono">{show(c.old_value)}</td>
              <td className="mono">{show(c.new_value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** A set of selected row ids, for the bulk bars. */
export function useSelection() {
  const [sel, setSel] = useState<Set<number>>(new Set());
  return {
    sel,
    has: (id: number) => sel.has(id),
    toggle: (id: number) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }),
    setAll: (ids: number[], on: boolean) => setSel((s) => { const n = new Set(s); for (const id of ids) { if (on) n.add(id); else n.delete(id); } return n; }),
    clear: () => setSel(new Set()),
  };
}

/** The header checkbox: checked when every row on the page is selected. */
export function SelectAll({ ids, selection }: { ids: number[]; selection: ReturnType<typeof useSelection> }) {
  const all = ids.length > 0 && ids.every((id) => selection.has(id));
  return <input type="checkbox" aria-label="Select every row on this page" checked={all} onChange={(e) => selection.setAll(ids, e.target.checked)} />;
}

/** One message line for a write's outcome: good or bad. */
export function Outcome({ msg, err }: { msg: string; err: string }) {
  if (!msg && !err) return null;
  return <p className={`msg ${err ? 'bad' : 'good'}`}>{err ? `⚠ ${err}` : msg}</p>;
}

/** What an import or preview did, and its messages. */
export function ImportOutcome({ r }: { r: { status: string; summary: Record<string, number>; messages: string[]; dryRun: boolean } }) {
  const entries = Object.entries(r.summary).filter(([, v]) => typeof v === 'number');
  return (
    <div className="edi-result">
      <p className={`msg ${r.dryRun ? 'note-msg' : 'good'}`}>{r.status}</p>
      <div className="iw-totals">{entries.map(([k, v]) => <span key={k}><span className="muted">{k.replace(/([A-Z])/g, ' $1').toLowerCase()}</span> <strong>{v.toLocaleString()}</strong></span>)}</div>
      {r.messages.length > 0 && (
        <ul className="import-messages">{r.messages.map((m, i) => <li key={i}>{m}</li>)}</ul>
      )}
    </div>
  );
}

/**
 * A set of categories, shown as tags and chosen from the tree — a bid's (Rebuild Products expands
 * them) or a master's (its own memberships). `ids` is the saved set, `shown` their names.
 */
export function CategoriesCard({ ids, shown, canEdit, onSave, note }: {
  ids: number[]; shown: { id: number; name: string | null }[]; canEdit: boolean; onSave: (ids: number[]) => void; note?: string;
}) {
  const [openPicker, setOpenPicker] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set(ids));
  useEffect(() => { setPicked(new Set(ids)); }, [ids.join()]);
  const cats = useLoad(() => (openPicker ? api.catalogCategories() : Promise.resolve([] as CategoryRow[])), [openPicker]);
  const [q, setQ] = useState('');
  const list = useMemo(() => (cats.data ?? []).filter((c) => !q || (c.name ?? '').toLowerCase().includes(q.toLowerCase())), [cats.data, q]);
  const dirty = [...picked].sort().join() !== [...ids].sort().join();
  return (
    <section className="card">
      <h2>Categories ({ids.length})</h2>
      <div className="picked-row">
        {shown.slice(0, 40).map((c) => <span key={c.id} className="pill cat-tag">{c.name ?? c.id}</span>)}
        {shown.length > 40 && <span className="muted">and {shown.length - 40} more</span>}
        {shown.length === 0 && <span className="muted">No category.</span>}
      </div>
      {note && <p className="muted small">{note}</p>}
      {canEdit && !openPicker && <button className="btn-sm ghost" onClick={() => setOpenPicker(true)}>Choose categories…</button>}
      {openPicker && (
        <>
          <div className="toolbar">
            <input className="search" placeholder="Filter categories…" value={q} onChange={(e) => setQ(e.target.value)} />
            <span className="muted">{picked.size} chosen</span>
            <span className="toolbar-end actions">
              <button className="btn-sm" disabled={!dirty} onClick={() => { onSave([...picked]); setOpenPicker(false); }}>Save categories</button>
              <button className="link" onClick={() => { setPicked(new Set(ids)); setOpenPicker(false); }}>Cancel</button>
            </span>
          </div>
          <div className="pick-list">
            {list.map((c) => (
              <label key={c.id} className="cat-opt" style={{ paddingLeft: 8 + Math.max(0, c.level - 2) * 16 }}>
                <input type="checkbox" checked={picked.has(c.id)} onChange={(e) => {
                  const n = new Set(picked); if (e.target.checked) n.add(c.id); else n.delete(c.id); setPicked(n);
                }} />
                {c.name ?? `Category ${c.id}`} <span className="muted small">{c.masters.toLocaleString()}</span>{c.is_active === false ? <span className="muted small"> · inactive</span> : null}
              </label>
            ))}
            {!cats.data && <p className="loading">Loading…</p>}
          </div>
        </>
      )}
    </section>
  );
}

/** Production's Quick Quote category (quick_quote_add/general/category_id, default 1727 "Quick Quotes"). */
export const QUICK_QUOTE_CATEGORY = 1727;

/** One category from the tree, indented by depth — Add Quick Quote's. */
export function CategorySelect({ value, onChange }: { value: number | null; onChange: (id: number | null) => void }) {
  const cats = useLoad(() => api.catalogCategories(), []);
  // A tree without the configured category (the ETL not loaded here yet): file the quote nowhere.
  useEffect(() => {
    if (cats.data && value != null && !cats.data.some((c) => c.id === value)) onChange(null);
  }, [cats.data]);
  return (
    <label>Category
      <select value={value ?? ''} disabled={!cats.data?.length} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
        {!cats.data?.length && <option value="">{cats.data ? 'No categories loaded' : 'Loading…'}</option>}
        {cats.data?.map((c) => <option key={c.id} value={c.id}>{'\u2003'.repeat(Math.max(0, c.level - 2))}{c.name ?? `Category ${c.id}`}</option>)}
      </select>
    </label>
  );
}
